import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, Circle, Lock, Unlock, Sparkles, ArrowRight, Clock, AlertCircle, SkipForward, Zap, FileText, Calendar, CheckSquare } from 'lucide-react';
import { format, isPast, parseISO, differenceInDays } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

/**
 * SubWorkflowItem
 *
 * Level 2: Sub-Workflow Display with Steps
 * Shows sub-workflow header and expandable step list
 */
const SubWorkflowItem = ({ subWorkflow, workflowKey, onTaskUpdate, onSubWorkflowComplete, navigate }) => {
  const [isExpanded, setIsExpanded] = useState(subWorkflow.isExpanded || false);
  const [isCompleting, setIsCompleting] = useState(false);

  const toggleExpand = () => {
    if (!subWorkflow.isLocked) {
      setIsExpanded(!isExpanded);
    }
  };

  // Check if completed
  const isCompleted = subWorkflow.status === 'completed';

  // Handler for completing entire subworkflow
  const handleCompleteSubworkflow = async (e) => {
    e.stopPropagation(); // Prevent triggering expand/collapse

    if (!onSubWorkflowComplete || isCompleting || subWorkflow.isLocked) return;

    // Confirm with user
    const incompleteTasks = subWorkflow.steps.filter(
      step => step.status !== 'completed' && step.status !== 'cancelled' && step.status !== 'skipped'
    );

    if (incompleteTasks.length === 0) {
      toast.success('All tasks already completed!');
      return;
    }

    const mandatoryCount = incompleteTasks.filter(
      step => step.workflow_metadata?.importance_level === 'mandatory'
    ).length;
    const optionalCount = incompleteTasks.length - mandatoryCount;

    setIsCompleting(true);

    try {
      const result = await onSubWorkflowComplete(
        subWorkflow.subWorkflowKey,
        workflowKey,
        subWorkflow.steps
      );

      if (result.success) {
        const messages = [];
        if (result.completed > 0) messages.push(`${result.completed} completed`);
        if (result.skipped > 0) messages.push(`${result.skipped} skipped`);

        toast.success(`Subworkflow complete: ${messages.join(', ')}`);
      } else {
        toast.error('Failed to complete subworkflow');
      }
    } catch (error) {
      console.error('Error completing subworkflow:', error);
      toast.error('Error completing subworkflow');
    } finally {
      setIsCompleting(false);
    }
  };

  // Check if has overdue tasks
  const hasOverdue = subWorkflow.steps?.some(step => {
    const dueDate = step.due_date || step.scheduled_date;
    return dueDate && isPast(parseISO(dueDate)) && step.status !== 'completed' && step.status !== 'cancelled' && step.status !== 'skipped';
  });

  // Detect mobile - use state and listener for responsive updates
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Level 2 (Essay Workflow) styling - 1px slate-500 border
  const getStatusStyle = () => {
    // Completed and collapsed: slim, grayed out
    if (isCompleted && !isExpanded) {
      return {
        border: '1px solid #64748b',
        background: 'white',
        opacity: 0.65
      };
    }
    if (subWorkflow.isPrerequisite) {
      return {
        border: '1px solid #7e57c2',
        background: '#f3f0ff'
      };
    }
    if (subWorkflow.isLocked) {
      return {
        border: '1px solid #cbd5e1',
        background: 'white',
        opacity: 0.6
      };
    }
    // Level 2: Medium weight - 1px slate-500 border
    return {
      border: '1px solid #64748b',
      background: 'white'
    };
  };

  const statusStyle = getStatusStyle();

  // Badge for status
  const getBadge = () => {
    if (subWorkflow.status === 'completed') {
      return null; // No badge needed, checkbox shows completion
    }
    if (subWorkflow.isPrerequisite) {
      return { text: 'PREREQUISITE', style: { background: '#7e57c2', color: 'white' } };
    }
    if (subWorkflow.isLocked) {
      return { text: '🔒 LOCKED', style: { background: '#f5f5f4', color: '#78716c' } };
    }
    if (subWorkflow.status === 'in_progress') {
      return { text: 'CURRENT', style: { background: '#ffd54f', color: '#92400e' } };
    }
    return null;
  };

  const badge = getBadge();

  return (
    <div
      style={{
        ...statusStyle,
        // Level 2: rounded-xl (12px) like mockup
        borderRadius: isCompleted && !isExpanded ? '8px' : '12px',
        marginBottom: isCompleted && !isExpanded ? '0.5rem' : '1rem',
        overflow: 'hidden',
        transition: 'all 0.15s ease',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' // Subtle shadow for Level 2
      }}
      onMouseEnter={(e) => {
        if (!subWorkflow.isLocked && !isExpanded) {
          e.currentTarget.style.borderColor = '#64748b';
          e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
          if (isCompleted) {
            e.currentTarget.style.opacity = 1;
          }
        }
      }}
      onMouseLeave={(e) => {
        if (!isExpanded) {
          e.currentTarget.style.borderColor = statusStyle.border.split(' ')[2];
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
          if (isCompleted) {
            e.currentTarget.style.opacity = 0.65;
          }
        }
      }}
    >
      {/* Sub-Workflow Header */}
      <div
        onClick={toggleExpand}
        style={{
          // Level 2: slate-100 background (lighter gray)
          background: '#f1f5f9',
          padding: isMobile
            ? (isCompleted && !isExpanded ? '0.5rem 0.75rem' : '0.75rem')
            : (isCompleted && !isExpanded ? '0.625rem 1rem' : '1rem'),
          cursor: subWorkflow.isLocked ? 'not-allowed' : 'pointer',
          display: 'flex',
          gap: isMobile ? '0.75rem' : '1rem',
          alignItems: 'flex-start',
          transition: 'all 0.15s ease',
          borderBottom: isExpanded ? '2px solid #cbd5e1' : 'none'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#e2e8f0';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#f1f5f9';
        }}
      >
        {/* Icon - Level 2: FileText icon in teal-100 background */}
        <div style={{
          width: isMobile ? '28px' : '32px',
          height: isMobile ? '28px' : '32px',
          // Teal-100 background with teal-400 border like mockup
          background: isCompleted ? '#cbd5e1' : '#ccfbf1',
          border: isCompleted ? '1px solid #94a3b8' : '1px solid #5eead4',
          borderRadius: isMobile ? '6px' : '8px',
          flexShrink: 0,
          marginTop: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.15s ease'
        }}>
          <FileText size={isMobile ? 14 : 16} color={isCompleted ? '#64748b' : '#0f766e'} />
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            // Level 2: Medium weight (font-bold, text-sm in mockup)
            fontWeight: 600,
            color: isCompleted && !isExpanded ? '#71717a' : '#0f172a',
            fontSize: isMobile ? '0.8125rem' : '0.875rem',
            marginBottom: isMobile ? '0.125rem' : '0.25rem',
            transition: 'all 0.15s ease',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isMobile ? 2 : 3,
            WebkitBoxOrient: 'vertical'
          }}>
            {subWorkflow.title}
          </div>
          {/* Details row - step count and total time */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '0.75rem',
            fontSize: isMobile ? '0.6875rem' : '0.75rem',
            color: '#64748b',
            flexWrap: 'wrap'
          }}>
            <span>
              {subWorkflow.totalSteps} step{subWorkflow.totalSteps !== 1 ? 's' : ''}
              {subWorkflow.currentStep && ` · Step ${subWorkflow.currentStepIndex || 1} of ${subWorkflow.totalSteps} in progress`}
              {!subWorkflow.currentStep && (subWorkflow.status === 'planned' || subWorkflow.status === 'not_started') && ' · Not started'}
            </span>
            {subWorkflow.estimatedHours && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#64748b' }}>
                <Clock size={12} />
                ~{subWorkflow.estimatedHours} hr total
              </span>
            )}
          </div>
          {/* Badges - Only show when expanded or not completed */}
          {(!isCompleted || isExpanded) && (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {badge && (
                  <span style={{
                    ...badge.style,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '10px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}>
                    {badge.text}
                  </span>
                )}
              </div>

              {/* Contextual Tip */}
              {subWorkflow.contextualTip && !subWorkflow.isLocked && (
                <div style={{
                  color: '#64748b',
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  marginTop: '0.5rem'
                }}>
                  {subWorkflow.contextualTip}
                </div>
              )}

              {/* Unlock Preview */}
              {subWorkflow.isPrerequisite && subWorkflow.unlockDescription && subWorkflow.status !== 'completed' && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: '#eff7ff',
                  borderLeft: '3px solid #4290f5',
                  borderRadius: '0 6px 6px 0'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#1e59d7',
                    marginBottom: '0.5rem'
                  }}>
                    🔓 Completing this unlocks:
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#1e59d7'
                  }}>
                    {subWorkflow.unlockDescription}
                  </div>
                </div>
              )}
            </>
          )}
          {/* Show simple completed badge when collapsed and completed */}
          {isCompleted && !isExpanded && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              fontSize: '0.7rem',
              fontWeight: 600,
              background: '#e7e5e4',
              color: '#78716c',
              marginTop: '0.25rem'
            }}>
              ✓ Completed
            </span>
          )}
        </div>

        {/* Overdue Badge and Expand Icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.5rem' : '0.75rem', flexShrink: 0 }}>
          {/* Complete Subworkflow Button - Only show when expanded and not completed/locked */}
          {isExpanded && !isCompleted && !subWorkflow.isLocked && onSubWorkflowComplete && (
            <button
              onClick={handleCompleteSubworkflow}
              disabled={isCompleting}
              title="Mark entire subworkflow complete"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: isMobile ? '0.25rem' : '0.375rem',
                padding: isMobile ? '0.25rem 0.5rem' : '0.375rem 0.625rem',
                background: isCompleting ? '#e2e8f0' : '#14b8a6',
                border: '1.5px solid #0f766e',
                borderRadius: '6px',
                fontSize: isMobile ? '0.6875rem' : '0.75rem',
                fontWeight: 600,
                color: 'white',
                cursor: isCompleting ? 'wait' : 'pointer',
                transition: 'all 0.2s ease',
                flexShrink: 0,
                opacity: isCompleting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!isCompleting) {
                  e.currentTarget.style.background = '#0d9488';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCompleting) {
                  e.currentTarget.style.background = '#14b8a6';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <CheckSquare size={isMobile ? 12 : 14} strokeWidth={2.5} />
              {isMobile ? 'Done' : 'Complete All'}
            </button>
          )}

          {/* Overdue badge (mockup shows "2 overdue") */}
          {hasOverdue && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: isMobile ? '0.125rem 0.5rem' : '0.25rem 0.625rem',
              borderRadius: '9999px',
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              fontSize: isMobile ? '0.625rem' : '0.6875rem',
              fontWeight: 500,
              color: '#b45309',
              whiteSpace: 'nowrap'
            }}>
              <AlertCircle size={isMobile ? 10 : 12} />
              {subWorkflow.steps?.filter(s => {
                const dueDate = s.due_date || s.scheduled_date;
                return dueDate && isPast(parseISO(dueDate)) && s.status !== 'completed';
              }).length || 0} {isMobile ? '' : 'overdue'}
            </span>
          )}

          {/* Expand Icon */}
          {!subWorkflow.isLocked && (
            <div style={{
              width: isMobile ? '18px' : '20px',
              height: isMobile ? '18px' : '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s ease',
              color: '#64748b',
              flexShrink: 0
            }}>
              {isExpanded ? <ChevronUp size={isMobile ? 18 : 20} /> : <ChevronDown size={isMobile ? 18 : 20} />}
            </div>
          )}
        </div>
      </div>

      {/* Steps Container */}
      <div style={{
        maxHeight: isExpanded ? '2000px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease',
        // Level 2: White background for steps container
        background: 'white',
        borderTop: isExpanded ? 'none' : 'none'
      }}>
        <div style={{ padding: '1.25rem', position: 'relative' }}>
          {/* Vertical progress line - Level 3 */}
          <div style={{
            position: 'absolute',
            left: '40px', // Position at center of numbered circles (20px left margin + 20px circle center)
            top: 0,
            bottom: 0,
            width: '2px',
            background: '#e2e8f0',
            zIndex: 0
          }} />

          {/* Steps with numbered circles */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            {subWorkflow.steps.map((step, index) => (
              <StepItem
                key={step.id}
                step={step}
                stepNumber={index + 1}
                isCurrent={subWorkflow.currentStep?.id === step.id}
                onTaskUpdate={onTaskUpdate}
                navigate={navigate}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * TimeEstimateDropdown - Interactive dropdown for Standard vs Express time estimates
 */
const TimeEstimateDropdown = ({ step, isMVVSelected, onToggleMVV, togglingMVV, isMobile }) => {
  const [isOpen, setIsOpen] = useState(false);

  const standardTime = Math.round(step.workflow_metadata.estimated_minutes / 60 * 10) / 10;
  const expressTime = step.mvv_time_required ? Math.round(step.mvv_time_required / 60 * 10) / 10 : null;
  const currentTime = isMVVSelected ? expressTime : standardTime;
  const currentMode = isMVVSelected ? 'Express' : 'Standard';

  const handleModeSelect = (isExpress) => {
    if (isExpress !== isMVVSelected) {
      onToggleMVV({ stopPropagation: () => {} });
    }
    setIsOpen(false);
  };

  return (
    <div style={{ marginTop: isMobile ? '0.5rem' : '0.75rem', position: 'relative' }}>
      {/* Summary button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={togglingMVV}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: isMobile ? '0.375rem' : '0.5rem',
          padding: isMobile ? '0.375rem 0.625rem' : '0.5rem 0.75rem',
          borderRadius: isMobile ? '8px' : '10px',
          background: 'white',
          border: '1px solid #14b8a6',
          fontSize: isMobile ? '0.6875rem' : '0.75rem',
          fontWeight: 500,
          color: '#475569',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          opacity: togglingMVV ? 0.6 : 1
        }}
        onMouseEnter={(e) => {
          if (!togglingMVV) {
            e.currentTarget.style.background = '#f0fdfa';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'white';
        }}
      >
        <Clock size={isMobile ? 12 : 14} color="#14b8a6" />
        <span style={{ fontWeight: 700, color: '#0f172a' }}>{currentTime} hr</span>
        <span style={{ color: '#64748b' }}>· {currentMode} Session</span>
        <ChevronDown
          size={isMobile ? 10 : 12}
          color="#64748b"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s ease'
          }}
        />
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div style={{
          marginTop: '0.5rem',
          padding: isMobile ? '0.625rem' : '0.75rem',
          background: 'white',
          borderRadius: isMobile ? '8px' : '10px',
          border: '1px solid #cbd5e1',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}>
          {/* Standard option */}
          <button
            onClick={() => handleModeSelect(false)}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: isMobile ? '0.625rem' : '0.75rem',
              borderRadius: isMobile ? '8px' : '10px',
              background: 'white',
              border: !isMVVSelected ? '1px solid #14b8a6' : '1px solid #cbd5e1',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '0.5rem',
              marginBottom: '0.25rem'
            }}>
              <span style={{
                fontSize: isMobile ? '0.6875rem' : '0.75rem',
                fontWeight: 700,
                color: '#0f172a'
              }}>
                Standard Session
              </span>
              <span style={{
                fontSize: isMobile ? '0.6875rem' : '0.75rem',
                fontWeight: 700,
                color: '#14b8a6'
              }}>
                {standardTime} hr
              </span>
            </div>
            <p style={{
              fontSize: isMobile ? '0.625rem' : '0.6875rem',
              color: '#475569',
              marginBottom: '0.375rem',
              lineHeight: 1.4
            }}>
              8-10 concepts with guided exploration
            </p>
            <p style={{
              fontSize: isMobile ? '0.625rem' : '0.6875rem',
              color: '#64748b',
              fontStyle: 'italic',
              lineHeight: 1.3
            }}>
              Best when you have time to think deeply
            </p>
          </button>

          {/* Express option */}
          {expressTime && (
            <button
              onClick={() => handleModeSelect(true)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: isMobile ? '0.625rem' : '0.75rem',
                borderRadius: isMobile ? '8px' : '10px',
                background: 'white',
                border: isMVVSelected ? '1px solid #64748b' : '1px solid #cbd5e1',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f5f5f4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '0.5rem',
                marginBottom: '0.25rem'
              }}>
                <span style={{
                  fontSize: isMobile ? '0.6875rem' : '0.75rem',
                  fontWeight: 700,
                  color: '#0f172a'
                }}>
                  Express Session
                </span>
                <span style={{
                  fontSize: isMobile ? '0.6875rem' : '0.75rem',
                  fontWeight: 700,
                  color: '#64748b'
                }}>
                  {expressTime} hr
                </span>
              </div>
              <p style={{
                fontSize: isMobile ? '0.625rem' : '0.6875rem',
                color: '#475569',
                marginBottom: '0.375rem',
                lineHeight: 1.4
              }}>
                3-4 core concepts, rapid ideation
              </p>
              <p style={{
                fontSize: isMobile ? '0.625rem' : '0.6875rem',
                color: '#64748b',
                fontStyle: 'italic',
                lineHeight: 1.3
              }}>
                Best when you need to keep momentum
              </p>
            </button>
          )}

          {/* Safety message */}
          <p style={{
            fontSize: isMobile ? '0.625rem' : '0.6875rem',
            color: '#64748b',
            textAlign: 'center',
            paddingTop: '0.25rem',
            margin: 0
          }}>
            You can always return and expand later
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * StepItem - Individual Step Display (Level 3)
 */
const StepItem = ({ step, stepNumber, isCurrent, onTaskUpdate, navigate }) => {
  const [togglingMVV, setTogglingMVV] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const isCompleted = step.status === 'completed';
  const isSkipped = step.status === 'cancelled' || step.status === 'skipped'; // Support both old and new
  const isOverdue = step.due_date && isPast(parseISO(step.due_date)) && !isCompleted && !isSkipped;
  const isMandatory = step.workflow_metadata?.importance_level === 'mandatory';
  const isLocked = step.is_locked;

  // Detect mobile - use state and listener for responsive updates
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if MVV is available and selected
  const hasMVV = step.mvv_time_required && step.mvv_time_required !== step.estimated_duration;
  const isMVVSelected = step.mvv_selected;
  const timeSaved = hasMVV ? (step.estimated_duration - step.mvv_time_required) / 60 : 0;

  const handleToggleComplete = () => {
    const newStatus = isCompleted ? 'planned' : 'completed';
    // Optimistic update - fire and forget
    onTaskUpdate(step.id, { status: newStatus });
  };

  const handleToggleSkip = (e) => {
    e.stopPropagation();
    const newStatus = isSkipped ? 'planned' : 'cancelled';
    // Optimistic update - fire and forget
    onTaskUpdate(step.id, { status: newStatus });
  };

  const handleDateChange = async (e) => {
    e.stopPropagation();
    const newDate = e.target.value;

    if (!newDate) return;

    // Check if past hard_deadline
    if (step.hard_deadline) {
      const hardDeadlineDate = parseISO(step.hard_deadline);
      const newScheduledDate = parseISO(newDate);

      if (newScheduledDate > hardDeadlineDate) {
        toast.error(`Cannot reschedule past hard deadline of ${format(hardDeadlineDate, 'MMM d, yyyy')}`);
        return;
      }
    }

    try {
      // Update both scheduled_date and due_date
      await onTaskUpdate(step.id, {
        scheduled_date: newDate,
        due_date: newDate
      });

      toast.success('Date updated successfully');
      setShowDatePicker(false);
    } catch (error) {
      console.error('Error updating date:', error);
      toast.error('Failed to update date');
    }
  };

  const handleDateClick = (e) => {
    e.stopPropagation();
    setShowDatePicker(!showDatePicker);
  };

  const handleToggleMVV = async (e) => {
    e.stopPropagation();
    setTogglingMVV(true);
    try {
      const newMVVState = !isMVVSelected;
      const { error } = await supabase
        .from('timeline_items')
        .update({ mvv_selected: newMVVState })
        .eq('id', step.id);

      if (error) throw error;

      // Update local state via callback
      onTaskUpdate(step.id, { mvv_selected: newMVVState });

      toast.success(newMVVState
        ? `MVV mode enabled - saves ${timeSaved.toFixed(1)}h`
        : 'Standard mode enabled'
      );
    } catch (error) {
      console.error('Error toggling MVV:', error);
      toast.error('Failed to toggle MVV mode');
    } finally {
      setTogglingMVV(false);
    }
  };

  const handleAIToolClick = (e) => {
    e.stopPropagation();
    // Navigate to AI tool if specified
    const aiToolRoute = step.workflow_metadata?.ai_tool_route;
    if (aiToolRoute && navigate) {
      navigate(aiToolRoute);
    }
  };

  const isInactive = isCompleted || isSkipped;

  return (
    <div
      style={{
        // Level 3: Step wrapper with numbered circle on left
        position: 'relative',
        paddingLeft: isMobile ? '44px' : '56px', // Mobile: smaller (32px circle + 12px gap)
        marginBottom: isInactive ? (isMobile ? '0.5rem' : '0.75rem') : (isMobile ? '0.5rem' : '0.75rem')
      }}
    >
      {/* Numbered Circle - Level 3 */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: isCurrent ? (isMobile ? '10px' : '12px') : (isMobile ? '6px' : '8px'),
        width: isMobile ? '32px' : '40px',
        height: isMobile ? '32px' : '40px',
        borderRadius: '50%',
        // Current: teal-500, Upcoming: white with border, Locked: stone-100
        background: isCurrent ? '#14b8a6' : isLocked ? '#f5f5f4' : 'white',
        border: isCurrent ? '1px solid white' : isLocked ? '1px solid #d6d3d1' : '1px solid #cbd5e1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isCurrent ? '0 4px 12px rgba(20, 184, 166, 0.3)' : 'none',
        zIndex: 10,
        transition: 'all 0.15s ease'
      }}>
        {isLocked ? (
          <Lock size={isMobile ? 14 : 16} color="#a8a29e" />
        ) : (
          <span style={{
            fontSize: isMobile ? '0.6875rem' : '0.75rem',
            fontWeight: 700,
            color: isCurrent ? 'white' : isLocked ? '#a8a29e' : '#64748b'
          }}>
            {stepNumber}
          </span>
        )}
      </div>

      {/* Step Card */}
      <div
        style={{
          display: 'flex',
          gap: isInactive ? (isMobile ? '0.375rem' : '0.5rem') : (isMobile ? '0.5rem' : '0.625rem'),
          padding: isMobile
            ? (isCurrent ? '0.75rem' : (isInactive ? '0.5rem' : '0.625rem'))
            : (isCurrent ? '1rem' : (isInactive ? '0.75rem' : '0.75rem')),
          // Current: teal-50 bg with teal-500 border | Upcoming: white with gray border | Inactive: reduced opacity
          background: isCurrent ? '#f0fdfa' : 'white',
          border: isCurrent ? '1px solid #14b8a6' : isInactive ? '1px solid #d4d4d8' : '1px solid #cbd5e1',
          borderRadius: isInactive ? '6px' : '8px',
          transition: 'all 0.15s ease',
          opacity: isInactive ? 0.55 : 1
        }}
        onMouseEnter={(e) => {
          if (!step.is_locked && !isInactive && !isCurrent) {
            e.currentTarget.style.borderColor = '#64748b';
            e.currentTarget.style.background = '#f8fafc';
          } else if (isInactive) {
            e.currentTarget.style.opacity = 0.75;
          }
        }}
        onMouseLeave={(e) => {
          if (!isCurrent && !isInactive) {
            e.currentTarget.style.borderColor = '#cbd5e1';
            e.currentTarget.style.background = 'white';
          } else if (isInactive) {
            e.currentTarget.style.opacity = 0.55;
          }
        }}
      >
      {/* Checkbox */}
      <div
        onClick={handleToggleComplete}
        style={{
          // Level 3: Current task gets bigger checkbox (16px), upcoming gets smaller (14px)
          // Mobile: slightly smaller (14px current, 12px upcoming)
          width: isMobile
            ? (isCurrent ? '14px' : '12px')
            : (isCurrent ? '16px' : '14px'),
          height: isMobile
            ? (isCurrent ? '14px' : '12px')
            : (isCurrent ? '16px' : '14px'),
          border: `2px solid ${isCompleted ? '#14b8a6' : isSkipped ? '#94a3b8' : isCurrent ? '#0d9488' : '#cbd5e1'}`,
          borderRadius: '4px',
          flexShrink: 0,
          marginTop: '2px',
          background: isCompleted ? '#14b8a6' : isSkipped ? '#f1f5f9' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: step.is_locked ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s ease'
        }}
      >
        {isCompleted && (
          <svg
            width={isMobile ? (isInactive ? '8' : '10') : (isInactive ? '10' : '12')}
            height={isMobile ? (isInactive ? '8' : '10') : (isInactive ? '10' : '12')}
            viewBox="0 0 12 12"
            fill="none"
            stroke="white"
            strokeWidth="2"
          >
            <path d="M2 6l2 2 6-6"/>
          </svg>
        )}
        {isSkipped && (
          <SkipForward size={isMobile ? (isInactive ? 7 : 9) : (isInactive ? 8 : 10)} color="#64748b" strokeWidth={2.5} />
        )}
      </div>

      {/* Step Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title row with time badge for upcoming steps */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: isMobile ? '0.375rem' : '0.5rem' }}>
          <div style={{
            // Level 3: Current task is larger and bolder, upcoming is smaller
            // Mobile: slightly smaller text
            fontWeight: isCurrent ? 600 : 500,
            color: isInactive ? '#71717a' : (isCurrent ? '#0f172a' : '#475569'),
            fontSize: isMobile
              ? (isCurrent ? '0.75rem' : '0.6875rem')
              : (isCurrent ? '0.875rem' : '0.75rem'),
            marginBottom: isInactive ? '0' : (isCurrent ? (isMobile ? '0.25rem' : '0.375rem') : '0'),
            transition: 'all 0.15s ease',
            lineHeight: 1.3,
            flex: 1
          }}>
            {step.title}
          </div>
          {/* Time badge for upcoming (non-current) steps - shown in top-right corner */}
          {!isCurrent && !isInactive && step.workflow_metadata?.estimated_minutes && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: isMobile ? '0.125rem 0.375rem' : '0.25rem 0.375rem',
              borderRadius: '4px',
              background: '#f5f5f4',
              fontSize: isMobile ? '0.625rem' : '0.6875rem',
              color: '#64748b',
              flexShrink: 0,
              marginTop: '-2px'
            }}>
              <Clock size={isMobile ? 8 : 10} />
              {Math.round(step.workflow_metadata.estimated_minutes / 60 * 10) / 10} hr
            </span>
          )}
        </div>
        {/* Only show badges and details when not inactive */}
        {!isInactive && (
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {isCurrent && (
              <span style={{
                background: '#ffd54f',
                color: '#92400e',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: isMobile ? '0.125rem 0.375rem' : '0.25rem 0.5rem',
                borderRadius: '10px',
                fontSize: isMobile ? '0.625rem' : '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                CURRENT
              </span>
            )}
            {isOverdue && (
              <span style={{
                background: '#fccfcf',
                color: '#7b1348',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: isMobile ? '0.125rem 0.375rem' : '0.25rem 0.5rem',
                borderRadius: '10px',
                fontSize: isMobile ? '0.625rem' : '0.7rem',
                fontWeight: 600,
                border: '1px solid #ff6b6b'
              }}>
                <AlertCircle size={isMobile ? 10 : 12} />
                Due {format(parseISO(step.due_date), 'MMM d')}
              </span>
            )}
            {/* Show scheduled date for all non-overdue tasks - clickable to change */}
            {!isOverdue && (step.scheduled_date || step.due_date) && (
              <div style={{ position: 'relative' }}>
                <span
                  onClick={handleDateClick}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: isMobile ? '0.125rem 0.375rem' : '0.25rem 0.5rem',
                    borderRadius: '10px',
                    fontSize: isMobile ? '0.625rem' : '0.7rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.color = '#1e293b';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#475569';
                  }}
                >
                  <Calendar size={isMobile ? 10 : 12} />
                  {format(parseISO(step.scheduled_date || step.due_date), 'MMM d')}
                </span>

                {/* Date picker overlay */}
                {showDatePicker && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '0.25rem',
                    zIndex: 50,
                    background: 'white',
                    border: '2px solid #0f172a',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)'
                  }}>
                    <input
                      type="date"
                      defaultValue={step.scheduled_date || step.due_date}
                      onChange={handleDateChange}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        border: '1px solid #cbd5e1',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        fontFamily: "'Reddit Sans', system-ui, sans-serif"
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Show simple status badge for inactive tasks */}
        {isInactive && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: isMobile ? '0.625rem' : '0.7rem',
            fontWeight: 600,
            color: isSkipped ? '#64748b' : '#78716c',
            marginTop: '0.125rem'
          }}>
            {isSkipped ? '⏭️ Skipped' : '✓'}
          </span>
        )}

        {/* Contextual Tip - Only show for current step */}
        {isCurrent && step.workflow_metadata?.contextual_tip && (
          <div style={{
            color: '#64748b',
            fontSize: isMobile ? '0.75rem' : '0.8125rem',
            marginTop: isMobile ? '0.375rem' : '0.5rem',
            lineHeight: 1.4
          }}>
            💡 {step.workflow_metadata.contextual_tip}
          </div>
        )}

        {/* Time Estimate Dropdown - Only for current step with MVV option */}
        {isCurrent && hasMVV && step.workflow_metadata?.estimated_minutes && (
          <TimeEstimateDropdown
            step={step}
            isMVVSelected={isMVVSelected}
            onToggleMVV={handleToggleMVV}
            togglingMVV={togglingMVV}
            isMobile={isMobile}
          />
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: isMobile ? '0.375rem' : '0.5rem', marginLeft: 'auto', alignItems: 'center' }}>
        {/* Skip Button - Only show for non-mandatory tasks that aren't completed */}
        {!isMandatory && !isCompleted && !step.is_locked && (
          <button
            onClick={handleToggleSkip}
            title={isSkipped ? 'Unskip this task' : 'Skip this task'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '0.25rem' : '0.375rem',
              padding: isMobile ? '0.25rem 0.5rem' : '0.375rem 0.625rem',
              background: isSkipped ? '#f1f5f9' : 'transparent',
              border: `1.5px solid ${isSkipped ? '#64748b' : '#cbd5e1'}`,
              borderRadius: '6px',
              fontSize: isMobile ? '0.6875rem' : '0.75rem',
              fontWeight: 600,
              color: isSkipped ? '#64748b' : '#94a3b8',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (!isSkipped) {
                e.currentTarget.style.borderColor = '#94a3b8';
                e.currentTarget.style.color = '#64748b';
                e.currentTarget.style.background = '#f8fafc';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSkipped) {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.color = '#94a3b8';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <SkipForward size={isMobile ? 10 : 12} strokeWidth={2.5} />
            {isSkipped ? 'Skipped' : 'Skip'}
          </button>
        )}

        {/* AI Support Button - Only show for current step */}
        {isCurrent && step.workflow_metadata?.ai_tool && (
          <button
            onClick={handleAIToolClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '0.375rem' : '0.5rem',
              padding: isMobile ? '0.375rem 0.5rem' : '0.5rem 0.75rem',
              paddingRight: isMobile ? '0.375rem' : '0.5rem',
              background: '#0f172a',
              border: 'none',
              borderRadius: isMobile ? '10px' : '12px',
              fontSize: isMobile ? '0.8125rem' : '0.875rem',
              fontWeight: 600,
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textDecoration: 'none',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1e293b';
              e.currentTarget.style.transform = 'translateX(-4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#0f172a';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.25rem' : '0.35rem' }}>
              <span style={{ fontSize: isMobile ? '14px' : '16px', animation: 'sparkle 2.5s ease-in-out infinite' }}>
                ✨
              </span>
              <span>{step.workflow_metadata.ai_tool}</span>
            </div>
            <div style={{
              width: isMobile ? '28px' : '32px',
              height: isMobile ? '28px' : '32px',
              background: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <ArrowRight size={isMobile ? 14 : 16} color="#0f172a" strokeWidth={2.5} />
            </div>
          </button>
        )}
      </div>
      </div>
    </div>
  );
};

export default SubWorkflowItem;
